import React, { useEffect, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { Button, Typography, Carousel, Image } from 'antd';
import { TailSpin } from 'react-loader-spinner';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { CloseCircleFilled } from '@ant-design/icons';

import { getAllWhatsNew } from '../api/requests/whatsnew';
import useAuth from '../hooks/useAuth';
import useCustomToken from '../hooks/useCustomToken';
import { getRawClientName, getS3Name } from '../static/common';
import { generateKey } from '../utils/utils';
import CustomDataModal from './CustomDataModal';
import { StorageKeys, UserActivityTypes } from '../constants';
import { updateUserActivity } from '../api/requests/user';

interface CarouselInstance {
  next: () => void;
  prev: () => void;
  goTo: (slide: number, dontAnimate?: boolean) => void;
  autoPlay: () => void;
  innerSlider: any;
}

const { Paragraph, Text, Title } = Typography;
const DatasetImage = 'assets/images/newDataset.png';

const StyledCarousel = styled(Carousel)`
  padding: 1rem 2rem;

  && .slick-slide {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  && .slick-current {
    margin-bottom: 1rem;
  }

  && .slick-list {
    border-radius: 8px;
  }

  && .slick-dots button, .slick-dots li.slick-active button {
    background-color: blue;
  }
`;

const CloseButton = styled(Button) <{ $token: any }>`
  position: absolute;
  top: 4px;
  right: 10px;
  background: transparent;
  border: none;
  font-size: 18px;
  color:${(props) => props.$token?.colorBgContainer};
  cursor: pointer;

  &:hover {
    color: ${(props) => props.$token?.colorErrorHover} !important;
  }
`;

const Box = styled.div<{ $token: any }>`
  height: 440px;
  overflow: hidden;
  background: ${(props) => props.$token?.colorBgContainer};
  && .ant-image {
    height: 100%;
    width: 100%;
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;
const AnimatedWrapper = styled.div<{ $token: any }>`
  background-color: ${(props) => props.$token?.colorBgElevated};
  max-width: 90%;
  margin: 1rem auto;
  animation: ${fadeIn} 0.5s ease-in-out;
`;

const StyledHeading = styled(Text)`
  font-size: 1.25rem;
  font-weight: bold;
  color: #333;
  margin-bottom: 1rem;
`;

const StyledParagraph = styled(Paragraph)`
  font-size: 1rem;
  line-height: 1.6;
  color: #666;
  margin-bottom: 1rem;
`;

const PopupDescription = styled.div<{ $token: any }>`
  background:${(props) => props.$token?.colorBgElevated};
  max-width: 90%;
  margin: 1rem auto;
  animation: ${fadeIn} 0.5s ease-in-out;
`;

const ClientNameTitle = styled(Title)`
  margin-bottom: 1rem;
`;

const ProductName = styled(Paragraph)`
  margin: 0.5rem 0;
  font-weight: 500;
  color: #333;
`;

const DatasetList = styled.ul`
  margin-left: 1.5rem;
  padding-left: 0.5rem;
  list-style-type: disc;
  li {
    margin-bottom: 0.3rem;
    color: #666;
    font-size: 0.95rem;
  }
`;

const CardImage = styled(Image)`
  height: 100% !important;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  margin: 0 auto;
`;

const ScrollableWrapper = styled.div`
  max-height: 200px;
  overflow-y: auto;
  padding-right: 8px;
`;
const StyledBanner = styled.div<{ $token: any }>`
  background-color: ${(props) => props.$token?.white};
  padding: 16px;
  margin-bottom: 16px;
  border-radius: 12px;
  text-align: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  font-size: 16px;
  font-weight: 500;
`;

const BannerTitle = styled.h2<{ $token: any }>`
  margin: 0;
  font-size: 20px;
  color: ${(props) => props.$token?.black};
`;

const BannerMessage = styled.p<{ $token: any }>`
  margin: 8px 0 0;
  font-size: 16px;
  color: ${(props) => props.$token?.colorTextSecondary};
`;

export const WhatsNewPopup = () => {
  const { t } = useTranslation();
  const token = useCustomToken();
  const { user, productsAccess, clientId } = useAuth();
  const carouselRef = useRef<CarouselInstance | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [contentItems, setContentItems] = useState<any[]>([]);

  const hasSeenPopup = (clientIdToCheck: string): boolean => {
    const popUpSeenData = localStorage.getItem(StorageKeys.loggedInTime);
    if (!popUpSeenData) return false;
    try {
      const seenClients = JSON.parse(popUpSeenData);
      return Array.isArray(seenClients) && seenClients.includes(clientIdToCheck);
    } catch {
      localStorage.removeItem(StorageKeys.loggedInTime);
      return false;
    }
  };
  const markPopupAsSeen = (clientIdToMark: string): void => {
    const popUpSeenData = localStorage.getItem(StorageKeys.loggedInTime);
    let seenClients: string[] = [];
    try {
      if (popUpSeenData) {
        seenClients = JSON.parse(popUpSeenData);
      }
      if (!Array.isArray(seenClients)) {
        seenClients = [];
      }
      if (!seenClients.includes(clientIdToMark)) {
        seenClients.push(clientIdToMark);
      }
      localStorage.setItem(StorageKeys.loggedInTime, JSON.stringify(seenClients));
    } catch {
      localStorage.removeItem(StorageKeys.loggedInTime);
    }
  };

  const whatsNewQuery = useQuery({
    queryKey: ['whatsNewQuery'],
    queryFn: () => getAllWhatsNew(clientId),
    refetchOnWindowFocus: false,
    enabled: !hasSeenPopup(clientId || '')
  });

  const loading = whatsNewQuery.isLoading;

  const processContentData = () => {
    const matchedClient = user?.client?.find((item: any) => item.id === clientId);
    if (!matchedClient) {
      setContentItems([]);
      return;
    }

    const lastActive = matchedClient?.lastPopUpSeen;
    const shouldShowPopup = !hasSeenPopup(clientId || '');

    if (!shouldShowPopup) {
      setContentItems([]);
      return;
    }

    const processWhatsNewContent = (whatsNewData: any[], matchedClientData: any, lastSeenPopup: any) => {
      if (!whatsNewData?.length) return [];

      const filteredData = whatsNewData.filter((item) => {
        if (!item.isPublish) return false;
        if (matchedClientData?.UserClient?.eula?.current?.timeStamp > item.publishedDate) return false;

        const isGlobalRelease = item.clientId === null;
        const isClientSpecificRelease = clientId && item.clientId === clientId;

        if (!lastSeenPopup) {
          return isGlobalRelease || isClientSpecificRelease;
        }

        return (isGlobalRelease || isClientSpecificRelease) && item.publishedDate > lastSeenPopup;
      });

      const combinedReleasePopups = filteredData
        .map((item) => item?.releasePopup)
        .flat()
        .filter((popup) => popup !== null && popup !== undefined)
        .filter((item) => {
          if (!item?.selectedProducts || item?.selectedProducts.length === 0) {
            return true;
          }
          return item.selectedProducts.some((product: any) => productsAccess.includes(product));
        });

      return combinedReleasePopups || [];
    };

    const processDatasetContent = (matchedClientData: any, lastSeenPopup: any) => {
      if (!matchedClientData) return [];
      const productList = matchedClientData?.clientproducts?.filter((product: any) => {
        const datasetDetails = product?.dataset?.filter((dataset: any) => {
          if (matchedClientData.UserClient?.eula?.current?.timeStamp > dataset?.ClientProductDataset?.updatedAt) {
            return false;
          }
          return (!lastSeenPopup) || (dataset?.ClientProductDataset?.updatedAt > lastSeenPopup);
        });

        return datasetDetails?.length > 0;
      }).map((product: any) => ({
        ...product,
        dataset: product?.dataset?.filter((dataset: any) => (
          (!lastSeenPopup) || (dataset?.ClientProductDataset?.updatedAt > lastSeenPopup)
        ))
      }));

      if (!productList?.length) return [];

      return [{
        type: 'dataset',
        clientName: getRawClientName(matchedClient.name),
        products: productList
      }];
    };
    const whatsNewItems = processWhatsNewContent(whatsNewQuery.data?.data?.rows, matchedClient, lastActive);
    // const datasetItems = processDatasetContent(matchedClient, lastActive);
    if (whatsNewItems.length === 0) {
      markPopupAsSeen(clientId || '');
    } else {
      setContentItems([...whatsNewItems]);
    }
  };

  useEffect(() => {
    if (whatsNewQuery.data && user?.client) {
      processContentData();
    }
  }, [whatsNewQuery.data]);

  const getListCount = (products: any[], productName: string) => products?.filter((item: any) => item?.dataset?.length > 0)
    ?.findIndex((item: any) => item?.product?.name === productName);
  const handleNext = () => {
    if (carouselRef.current) {
      const nextIndex = Math.min(currentIndex + 1, contentItems.length - 1);
      setCurrentIndex(nextIndex);
      carouselRef.current.goTo(nextIndex);
    }
  };

  const handlePrevious = () => {
    if (carouselRef.current) {
      const prevIndex = Math.max(currentIndex - 1, 0);
      setCurrentIndex(prevIndex);
      carouselRef.current.goTo(prevIndex);
    }
  };

  const handleClose = async () => {
    setContentItems([]);
    markPopupAsSeen(clientId || '');
    await updateUserActivity(UserActivityTypes.PopupAccess);
  };

  const renderDatasetContent = (datasetItem: any) => (
    <ScrollableWrapper>
      <StyledBanner $token={token}>
        <BannerTitle $token={token}>{t('whatsNew.newDatasetAdded')}</BannerTitle>
        <BannerMessage $token={token}>{t('whatsNew.newDatasetDescription')}</BannerMessage>
      </StyledBanner>
      <PopupDescription $token={token}>
        {datasetItem.products.map((product: any) => (
          <div key={generateKey()}>
            <ProductName>
              {`${getListCount(datasetItem.products, product.product.name) + 1}. ${product.product.name}`}
            </ProductName>
            <DatasetList>
              {product.dataset.map((dataset: any) => (
                <li key={generateKey()}>{dataset.name}</li>
              ))}
            </DatasetList>
          </div>
        ))}
      </PopupDescription>
    </ScrollableWrapper>
  );

  const renderContent = () => {
    const currentItem = contentItems[currentIndex];
    if (!currentItem) return null;
    if (currentItem.type === 'dataset') {
      return renderDatasetContent(currentItem);
    }

    return (
      <>
        <StyledHeading strong>
          {currentItem?.heading || ''}
        </StyledHeading>
        <StyledParagraph>
          {currentItem?.shortDescription || ''}
        </StyledParagraph>
      </>
    );
  };

  const shouldShowPopup = !loading && contentItems.length > 0;

  return (
    <CustomDataModal
      title={t('whatsNew.whatsNew')}
      justifyPosition="flex-end"
      openModal={shouldShowPopup}
      width="800px"
      footer={[
        (contentItems.length !== 0 && currentIndex !== 0) && (
          <Button
            key={generateKey()}
            onClick={handlePrevious}
          >
            {t('whatsNewPopup.previous')}
          </Button>
        ),
        !(currentIndex === contentItems.length - 1 || contentItems.length === 0) && (
          <Button
            key={generateKey()}
            onClick={handleNext}
          >
            {t('whatsNewPopup.next')}
          </Button>
        ),
        (currentIndex === contentItems.length - 1) && (
          <Button
            key={generateKey()}
            type="primary"
            onClick={handleClose}
          >
            {t('general.done')}
          </Button>
        )
      ]}
    >
      {loading ? (
        <TailSpin color={token?.colorTextTertiary} />
      ) : (
        <>
          <StyledCarousel
            arrows={false}
            infinite={false}
            ref={carouselRef}
            afterChange={(current) => setCurrentIndex(current)}
            speed={500}
            cssEase="ease-in-out"
            className="carousel"
          >
            {contentItems.map((item: any, index: number) => (
              <Box
                key={generateKey()}
                $token={token}
                className="box"
              >
                {item?.imageFile ? (
                  <CardImage
                    src={getS3Name(item?.imageFile)}
                    alt={`Slide ${index}`}
                  />
                ) : (
                  <CardImage
                    src={DatasetImage}
                    alt="camsys logo"
                  />
                )}
              </Box>
            ))}
          </StyledCarousel>

          <AnimatedWrapper
            key={currentIndex}
            $token={token}
          >
            {renderContent()}
          </AnimatedWrapper>
        </>
      )}
      <CloseButton
        onClick={handleClose}
        $token={token}
        aria-label={t('general.close')}
      >
        <CloseCircleFilled />
      </CloseButton>
    </CustomDataModal>
  );
};
